import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Bundle, BundleLine, CartLine, Product, ProductVariant } from "@/types";
import { fetchBundles, putCartSnapshot } from "@/lib/api";
import { cloudGet, cloudSet } from "@/lib/telegram";
import { track } from "@/lib/analytics";
import { useI18n } from "@/i18n";
import { loadPersisted, savePersisted } from "@/lib/storage";
import { haptic } from "@/lib/telegram";
import { useCatalog } from "./CatalogContext";

const CART_KEY = "kms.cart";
const FAVORITES_KEY = "kms.favorites";
const PROMO_KEY = "kms.promo";
const BUNDLES_KEY = "kms.bundles";

export interface CartLineWithProduct extends CartLine {
  product: Product;
  variant: ProductVariant;
  lineTotalUzs: number;
}

export interface BundleLineWithData extends BundleLine {
  bundle: Bundle;
  lineTotalUzs: number;
}

interface CartContextValue {
  /** Active bundles (for the home rail and the cart). */
  bundles: Bundle[];
  bundleLines: BundleLine[];
  bundleLinesWithData: BundleLineWithData[];
  addBundle: (bundleId: string, quantity?: number) => void;
  updateBundleQuantity: (bundleId: string, quantity: number) => void;
  lines: CartLine[];
  linesWithProducts: CartLineWithProduct[];
  totalItems: number;
  subtotalUzs: number;
  addItem: (productId: string, variantId: string, quantity?: number) => void;
  updateQuantity: (productId: string, variantId: string, quantity: number) => void;
  removeItem: (productId: string, variantId: string) => void;
  clear: () => void;
  favorites: string[];
  isFavorite: (productId: string) => boolean;
  toggleFavorite: (productId: string) => void;
  /** Promo code applied in the cart; re-validated by the server at checkout. */
  promoCode: string | null;
  setPromoCode: (code: string | null) => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const { products } = useCatalog();
  const [lines, setLines] = useState<CartLine[]>(() => loadPersisted<CartLine[]>(CART_KEY) ?? []);
  const [favorites, setFavorites] = useState<string[]>(() => loadPersisted<string[]>(FAVORITES_KEY) ?? []);
  const [promoCode, setPromoCode] = useState<string | null>(() => loadPersisted<string | null>(PROMO_KEY) ?? null);
  const [bundleLines, setBundleLines] = useState<BundleLine[]>(() => loadPersisted<BundleLine[]>(BUNDLES_KEY) ?? []);
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const { locale } = useI18n();

  useEffect(() => {
    fetchBundles().then(setBundles).catch(() => setBundles([]));
  }, [locale]);
  useEffect(() => savePersisted(BUNDLES_KEY, bundleLines), [bundleLines]);

  // Same cart on phone and desktop via Telegram CloudStorage. On start, a
  // newer cloud copy wins; afterwards every change is written back.
  const cloudReady = React.useRef(false);
  useEffect(() => {
    cloudGet("cart").then((raw) => {
      try {
        const cloud = raw ? (JSON.parse(raw) as { at: number; lines: CartLine[]; bundles: BundleLine[] }) : null;
        const localAt = Number(localStorage.getItem("kms.cartAt") || 0);
        if (cloud && cloud.at > localAt) {
          setLines(cloud.lines ?? []);
          setBundleLines(cloud.bundles ?? []);
        }
      } catch {
        /* ignore a broken cloud copy */
      }
      cloudReady.current = true;
    });
  }, []);
  useEffect(() => {
    if (!cloudReady.current) return;
    const at = Date.now();
    try {
      localStorage.setItem("kms.cartAt", String(at));
    } catch {
      /* ignore */
    }
    const timer = setTimeout(() => {
      cloudSet("cart", JSON.stringify({ at, lines, bundles: bundleLines }));
      // Server copy for the one-time "forgotten cart" reminder.
      putCartSnapshot(lines, bundleLines);
    }, 1500);
    return () => clearTimeout(timer);
  }, [lines, bundleLines]);

  const addBundle = useCallback((bundleId: string, quantity = 1) => {
    haptic("light");
    setBundleLines((prev) =>
      prev.some((l) => l.bundleId === bundleId)
        ? prev.map((l) => (l.bundleId === bundleId ? { ...l, quantity: l.quantity + quantity } : l))
        : [...prev, { bundleId, quantity }],
    );
  }, []);

  const updateBundleQuantity = useCallback((bundleId: string, quantity: number) => {
    setBundleLines((prev) => (quantity <= 0 ? prev.filter((l) => l.bundleId !== bundleId) : prev.map((l) => (l.bundleId === bundleId ? { ...l, quantity } : l))));
  }, []);

  const bundleLinesWithData = useMemo<BundleLineWithData[]>(
    () =>
      bundleLines
        .map((l) => {
          const bundle = bundles.find((b) => b.id === l.bundleId);
          return bundle ? { ...l, bundle, lineTotalUzs: bundle.priceUzs * l.quantity } : null;
        })
        .filter((l): l is BundleLineWithData => l !== null),
    [bundleLines, bundles],
  );

  useEffect(() => savePersisted(CART_KEY, lines), [lines]);
  useEffect(() => savePersisted(FAVORITES_KEY, favorites), [favorites]);
  useEffect(() => savePersisted(PROMO_KEY, promoCode), [promoCode]);

  const addItem = useCallback((productId: string, variantId: string, quantity = 1) => {
    haptic("light");
    track("add_to_cart", productId);
    setLines((prev) => {
      const existing = prev.find((l) => l.productId === productId && l.variantId === variantId);
      if (existing) {
        return prev.map((l) =>
          l.productId === productId && l.variantId === variantId ? { ...l, quantity: l.quantity + quantity } : l,
        );
      }
      return [...prev, { productId, variantId, quantity }];
    });
  }, []);

  const updateQuantity = useCallback((productId: string, variantId: string, quantity: number) => {
    setLines((prev) => {
      if (quantity <= 0) {
        return prev.filter((l) => !(l.productId === productId && l.variantId === variantId));
      }
      return prev.map((l) => (l.productId === productId && l.variantId === variantId ? { ...l, quantity } : l));
    });
  }, []);

  const removeItem = useCallback((productId: string, variantId: string) => {
    haptic("selection");
    setLines((prev) => prev.filter((l) => !(l.productId === productId && l.variantId === variantId)));
  }, []);

  const clear = useCallback(() => {
    setLines([]);
    setBundleLines([]);
    setPromoCode(null);
  }, []);

  const toggleFavorite = useCallback((productId: string) => {
    haptic("selection");
    setFavorites((prev) => (prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]));
  }, []);

  const isFavorite = useCallback((productId: string) => favorites.includes(productId), [favorites]);

  // Prices here come from the catalog cache, for display only. The real
  // total is always recomputed server-side in `placeOrder` (see
  // lib/api.ts) — this client-side figure can legitimately be stale if a
  // price changed since the item was added, and checkout must surface that.
  // A line whose product/variant no longer exists in the cache (deleted or
  // not yet loaded) is silently dropped from the rendered cart rather than
  // crashing — it stays in `lines`/storage in case the catalog was just
  // still loading, and disappears from view for good once genuinely gone.
  const linesWithProducts = useMemo<CartLineWithProduct[]>(() => {
    return lines
      .map((line) => {
        const product = products.find((p) => p.id === line.productId);
        const variant = product?.variants.find((v) => v.id === line.variantId);
        if (!product || !variant) return null;
        return { ...line, product, variant, lineTotalUzs: variant.priceUzs * line.quantity };
      })
      .filter((l): l is CartLineWithProduct => l !== null);
  }, [lines, products]);

  const totalItems = useMemo(() => lines.reduce((sum, l) => sum + l.quantity, 0) + bundleLines.reduce((s, l) => s + l.quantity, 0), [lines, bundleLines]);
  const subtotalUzs = useMemo(
    () => linesWithProducts.reduce((sum, l) => sum + l.lineTotalUzs, 0) + bundleLinesWithData.reduce((s, l) => s + l.lineTotalUzs, 0),
    [linesWithProducts, bundleLinesWithData],
  );

  const value = useMemo(
    () => ({
      lines,
      linesWithProducts,
      totalItems,
      subtotalUzs,
      addItem,
      updateQuantity,
      removeItem,
      clear,
      favorites,
      isFavorite,
      toggleFavorite,
      promoCode,
      setPromoCode,
      bundles,
      bundleLines,
      bundleLinesWithData,
      addBundle,
      updateBundleQuantity,
    }),
    [lines, linesWithProducts, totalItems, subtotalUzs, addItem, updateQuantity, removeItem, clear, favorites, isFavorite, toggleFavorite, promoCode, bundles, bundleLines, bundleLinesWithData, addBundle, updateBundleQuantity],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
